import { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getValidToken } from '../utils/auth';
import { optionTradingViewUrl } from '../utils/tradingViewLink';

const API_URL = process.env.REACT_APP_API_URL || '';

function isTradingDay(d) {
  const day = d.getDay();
  return day !== 0 && day !== 6;
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
    timeZone: 'America/New_York',
  });
}

// Column definitions: every column sortable per the user's spec. `get`
// returns the raw sortable value; `render` the cell content.
const COLUMNS = [
  { key: 'entry_at', label: 'Entry Time', get: (t) => t.entry_at },
  { key: 'type', label: 'Type', get: (t) => t.type },
  { key: 'strike', label: 'Strike', get: (t) => t.strike },
  { key: 'entry_price', label: 'Entry', get: (t) => t.entry_price },
  { key: 'sl_price', label: 'SL', get: (t) => t.sl_price },
  { key: 'ts_stop', label: 'TS', get: (t) => t.ts_stop },
  { key: 'high_after', label: 'H from entry', get: (t) => t.high_after },
  { key: 'low_after', label: 'L from entry', get: (t) => t.low_after },
  { key: 'pnl_pct', label: '%', get: (t) => (t.pnl_pct == null ? -Infinity : t.pnl_pct) },
  { key: 'status', label: 'Status', get: (t) => (t.status === 'open' ? 2 : t.pnl > 0 ? 1 : 0) },
];

// Live "Backtest (SPX-Last 30 Min)" review widget: the Last30Bot engine
// (backend handlers/last30_bot.go) trades the crashed-contract ignition
// strategy for real (simulated fills, real ask/bid quotes, $5/side fee)
// every session 15:30-16:00 ET and records to last30_trades. This widget is
// pure review: pick a date, see that day's trades, click a row to chart the
// contract above. Polls every 15s while viewing today so live entries/exits
// appear as they happen.
export default function Last30Widget({ onSelect }) {
  const [date, setDate] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: 'entry_at', dir: 1 });
  const [selectedIdx, setSelectedIdx] = useState(null);

  const iso = toISODate(date);

  const fetchTrades = useCallback(async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/last30?date=${iso}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [iso]);

  useEffect(() => {
    setSelectedIdx(null);
    fetchTrades();
    // Live-poll only when looking at today - past days never change.
    if (iso !== toISODate(new Date())) return undefined;
    const id = setInterval(fetchTrades, 15000);
    return () => clearInterval(id);
  }, [fetchTrades, iso]);

  const trades = [...(data?.trades || [])].sort((a, b) => {
    const col = COLUMNS.find((c) => c.key === sort.key);
    const va = col.get(a), vb = col.get(b);
    if (va < vb) return -sort.dir;
    if (va > vb) return sort.dir;
    return 0;
  });

  const headerClick = (key) => {
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
  };

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <style>{`
        .l30dp-popper { z-index: 9999 !important; }
        .l30dp { background-color: #111827 !important; border: 1px solid #374151 !important; border-radius: 12px !important;
          font-family: inherit !important; font-size: 12px !important; box-shadow: 0 8px 32px rgba(0,0,0,0.7) !important;
          overflow: hidden !important; color: #d1d5db !important; }
        .l30dp .react-datepicker__header { background-color: #1f2937 !important; border-bottom: 1px solid #374151 !important; padding: 10px 0 6px !important; }
        .l30dp .react-datepicker__current-month { color: #e5e7eb !important; font-size: 13px !important; font-weight: 600 !important; }
        .l30dp .react-datepicker__month-select, .l30dp .react-datepicker__year-select {
          background-color: #1f2937 !important; color: #e5e7eb !important; border: 1px solid #4b5563 !important;
          border-radius: 4px !important; padding: 2px 6px !important; font-size: 12px !important; cursor: pointer !important; }
        .l30dp .react-datepicker__navigation-icon::before { border-color: #9ca3af !important; }
        .l30dp .react-datepicker__day-names { background-color: #1f2937 !important; display: flex !important; justify-content: center !important; padding: 0 8px !important; }
        .l30dp .react-datepicker__day-name { color: #6b7280 !important; font-size: 11px !important; width: 2rem !important; line-height: 2rem !important; text-align: center !important; margin: 0 2px !important; }
        .l30dp .react-datepicker__month { background-color: #111827 !important; margin: 6px 8px !important; }
        .l30dp .react-datepicker__week { display: flex !important; justify-content: center !important; }
        .l30dp .react-datepicker__day { background-color: transparent !important; color: #ffffff !important; -webkit-text-fill-color: #ffffff !important;
          width: 2rem !important; line-height: 2rem !important; text-align: center !important; margin: 0 2px !important;
          border-radius: 6px !important; font-size: 12px !important; }
        .l30dp .react-datepicker__day:hover:not(.react-datepicker__day--disabled) { background-color: #1d4ed8 !important; }
        .l30dp .react-datepicker__day--selected { background-color: #2563eb !important; font-weight: 700 !important; }
        .l30dp .react-datepicker__day--today:not(.react-datepicker__day--selected) { border: 1px solid #2563eb !important; color: #60a5fa !important; -webkit-text-fill-color: #60a5fa !important; }
        .l30dp .react-datepicker__day--disabled, .l30dp .react-datepicker__day--disabled:hover {
          background-color: transparent !important; color: #4b5563 !important; -webkit-text-fill-color: #4b5563 !important;
          text-decoration: line-through !important; cursor: not-allowed !important; }
        .l30dp .react-datepicker__day--outside-month { color: #374151 !important; -webkit-text-fill-color: #374151 !important; }
      `}</style>

      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          Backtest (SPX-Last 30 Min)
        </span>
        <DatePicker
          selected={date}
          onChange={(d) => d && setDate(d)}
          filterDate={isTradingDay}
          maxDate={new Date()}
          dateFormat="MM/dd/yyyy"
          calendarClassName="l30dp"
          popperClassName="l30dp-popper"
          popperPlacement="bottom-start"
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          customInput={
            <input readOnly
              className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500 cursor-pointer font-mono"
              style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151', width: '110px' }}
              onChange={() => {}} />
          }
        />
        {data && data.closed > 0 && (
          <>
            <span className="text-xs font-mono text-gray-400">{data.wins}/{data.closed} wins</span>
            <span className="text-xs font-mono font-semibold"
              style={{ color: data.total_pnl >= 0 ? '#22c55e' : '#ef4444' }}>
              {data.total_pnl >= 0 ? '+' : ''}${data.total_pnl.toFixed(0)}
            </span>
          </>
        )}
      </div>
      <div className="text-xs text-gray-500 mb-3">
        Live engine, simulated fills at real quotes: crashed $0.25–$0.50 near-money (OTM ≤25) 0DTE SPXW,
        ignition ≥1.5x + 3 ticks + 5x volume, entries 3:30–3:55 PM at ask (≤2.5x the low), exits at bid.
        SL −50%, trailing ladder +100%→lock +10% / +200%→+100% / +300%→+180% / +500%→+300%, force-close 4:00 PM.
        $5 fee added on buy and subtracted on sell. Click a column to sort, a row to chart the contract.
      </div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}

      <div className="overflow-x-auto" style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead className="sticky top-0" style={{ backgroundColor: '#111827' }}>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {COLUMNS.map((c) => (
                <th key={c.key}
                  onClick={() => headerClick(c.key)}
                  className={`py-2 pr-3 font-medium cursor-pointer select-none hover:text-gray-300 ${c.key === 'type' || c.key === 'status' ? 'text-left' : 'text-right'}`}>
                  {c.label}{sort.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 ? (
              <tr><td colSpan={COLUMNS.length} className="py-4 text-center text-gray-600">
                No trades recorded for this day
              </td></tr>
            ) : trades.map((t, i) => {
              const isOpen = t.status === 'open';
              const pnlColor = isOpen ? '#f59e0b' : t.pnl > 0 ? '#22c55e' : t.pnl < 0 ? '#ef4444' : '#9ca3af';
              return (
                <tr key={`${t.symbol}-${t.entry_at}`}
                  onClick={() => { setSelectedIdx(i); onSelect?.({ symbol: t.symbol, date: t.trade_date, trade: t }); }}
                  className="border-b hover:bg-gray-800/50 cursor-pointer"
                  style={{ borderColor: '#1f2937', backgroundColor: selectedIdx === i ? '#1f293780' : undefined }}>
                  <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(t.entry_at)}</td>
                  <td className="py-1.5 pr-3">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: (t.type === 'C' ? '#22c55e' : '#ef4444') + '22', color: t.type === 'C' ? '#22c55e' : '#ef4444' }}>
                      {t.type === 'C' ? 'CALL' : 'PUT'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono">
                    <a href={optionTradingViewUrl(t.symbol)} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()} title="Open on TradingView"
                      style={{ color: '#60a5fa', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                      {t.strike % 1 === 0 ? t.strike.toFixed(0) : t.strike.toFixed(1)}
                    </a>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.entry_price.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">${t.sl_price.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono"
                    style={{ color: t.ts_stop > 0 ? '#22c55e' : '#4b5563' }}>
                    {t.ts_stop > 0 ? `$${t.ts_stop.toFixed(2)}` : '—'}
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.high_after.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">${t.low_after.toFixed(2)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color: pnlColor }}>
                    {t.pnl_pct == null ? '—' : `${t.pnl_pct >= 0 ? '+' : ''}${t.pnl_pct.toFixed(0)}%`}
                  </td>
                  <td className="py-1.5 pr-3">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: pnlColor + '22', color: pnlColor }}
                      title={t.exit_reason ? `exit: ${t.exit_reason} @ $${t.exit_price?.toFixed(2)} ${formatTime(t.exit_at)}` : 'position open'}>
                      {isOpen ? 'OPEN' : t.pnl > 0 ? 'Success' : t.pnl < 0 ? 'Failed' : 'Flat'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
